import { supabase, saveCandidateToSupabase } from "../constants";
import { genUUID, BLANK_QUAL_SECTIONS } from "../shared";

// ─── CANDIDATURA DESDE UNA PERSONA DE LA BASE ────────────────────────────────
// Un único camino para meter en una posición a alguien que ya existe en
// `people`: lo usan "Añadir desde la base" (pestaña Candidatos) y "Pasar a
// candidato" (Mapping). Los datos no se reescriben: se copian de lo que ya
// sabemos de esa persona, y la candidatura queda enlazada por person_id, que
// es lo que la junta con su ficha de Database.

const pick = (...vals) => vals.find(v => typeof v === "string" && v.trim()) || "";

// Lo que sabemos de una persona, de sus tres tablas. Manda el contacto
// (`contactId` si viene uno concreto, si no el más reciente), luego la cuenta
// del portal y luego una candidatura suya en otra posición.
export const loadPersonData = async (personId, contactId = null) => {
  const ctQ = contactId
    ? supabase.from("contacts").select("id, person_id, name, company, position, city, email, phone, linkedin").eq("id", contactId)
    : supabase.from("contacts").select("id, person_id, name, company, position, city, email, phone, linkedin").eq("person_id", personId).order("created_at", { ascending: false }).limit(1);
  const [{ data: ct }, { data: pe }, { data: cp }, { data: cd }] = await Promise.all([
    ctQ,
    personId ? supabase.from("people").select("id, full_name").eq("id", personId).maybeSingle() : Promise.resolve({ data: null }),
    personId ? supabase.from("candidate_profiles").select("full_name, email, phone, city, linkedin").eq("person_id", personId).limit(1) : Promise.resolve({ data: [] }),
    personId ? supabase.from("candidates").select("name, company, current_position, city, email, phone, linkedin").eq("person_id", personId).limit(1) : Promise.resolve({ data: [] }),
  ]);
  const c = ct?.[0] || {}, p = cp?.[0] || {}, k = cd?.[0] || {};
  return {
    personId: personId || c.person_id || null,
    name: pick(c.name, pe?.full_name, p.full_name, k.name),
    company: pick(c.company, k.company),
    position: pick(c.position, k.current_position),
    city: pick(c.city, p.city, k.city),
    email: pick(c.email, p.email, k.email),
    phone: pick(c.phone, p.phone, k.phone),
    linkedin: pick(c.linkedin, p.linkedin, k.linkedin),
  };
};

// ¿Ya es candidata de esta posición? Por person_id o por email.
export const findExistingCandidacy = async (positionId, person) => {
  const { data } = await supabase.from("candidates").select("id, name, email, person_id").eq("position_id", positionId);
  const email = (person.email || "").trim().toLowerCase();
  return (data || []).find(r =>
    (person.personId && r.person_id && String(r.person_id) === String(person.personId)) ||
    (email && (r.email || "").trim().toLowerCase() === email)
  ) || null;
};

// La foto se guarda por ficha (candidate_photos.candidate_id), no por persona:
// una candidatura nueva no tiene ninguna aunque la persona ya tenga foto en
// otra candidatura, en su cuenta del portal o en su contacto. Se copia la
// primera que haya (la URL, no el archivo). Devuelve la URL o null.
export const copyPersonPhoto = async (personId, newCandidateId) => {
  if (!personId || !newCandidateId) return null;
  const [{ data: cd }, { data: cp }, { data: ct }] = await Promise.all([
    supabase.from("candidates").select("id").eq("person_id", personId),
    supabase.from("candidate_profiles").select("id").eq("person_id", personId),
    supabase.from("contacts").select("id").eq("person_id", personId),
  ]);
  const ids = [...(cd || []), ...(cp || []), ...(ct || [])].map(r => String(r.id)).filter(id => id !== String(newCandidateId));
  if (!ids.length) return null;
  const { data: photos } = await supabase.from("candidate_photos").select("candidate_id, photo_url").in("candidate_id", ids);
  const url = (photos || []).find(p => p.photo_url)?.photo_url;
  if (!url) return null;
  const { data, error } = await supabase.from("candidate_photos")
    .upsert({ candidate_id: String(newCandidateId), photo_url: url, updated_at: new Date().toISOString() }, { onConflict: "candidate_id" })
    .select("candidate_id");
  return !error && data?.length ? url : null;
};

// Crea la candidatura (status pending) con el flujo de siempre y la enlaza a
// la persona. Devuelve { nc } con el candidato en el formato de la app, o
// { error }. `warn` si la candidatura se creó pero el enlace no se guardó.
// `photoUrl` si heredó la foto de la persona.
export const createCandidateFromPerson = async (person, position) => {
  const nc = {
    id: genUUID(), name: person.name || "", company: person.company || "", position: person.position || "", city: person.city || "",
    salary: "", notice: "", email: person.email || "", phone: person.phone || "", linkedin: person.linkedin || "",
    appDate: new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" }), status: "pending",
    scoring: { technical: 0, soft: 0, cultural: 0, growth: 0 }, verdict: "—", strengths: [], weaknesses: [],
    qualSections: BLANK_QUAL_SECTIONS.map(s => ({ ...s, items: s.items.map(it => ({ ...it, a: it.n === "1.1" ? (person.name || "") : it.n === "1.2" ? (person.city || "") : "" })) })),
    refsData: [], references: 0,
  };
  const saved = await saveCandidateToSupabase(nc, position.id, position.clientId);
  if (saved?.error) return { error: saved.error };
  let warn = null;
  if (person.personId) {
    const { data, error } = await supabase.from("candidates").update({ person_id: person.personId }).eq("id", nc.id).select("id");
    if (error || !data?.length) warn = error?.message || "person_id";
  }
  const photoUrl = await copyPersonPhoto(person.personId, nc.id);
  return { nc, warn, photoUrl };
};
